/**
 * Billing Dashboard Component
 *
 * Provides system admins with comprehensive billing oversight including:
 * - Revenue metrics (MRR, monthly revenue)
 * - Active subscription counts
 * - Payment failures requiring attention
 * - Recent billing activity
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CompanyBillingManagement } from './CompanyBillingManagement';

interface GroupBreakdown {
  groupName: string;
  mrr: number;
  count: number;
}

interface RevenueReport {
  totalMRR: number;
  standardMRR: number;
  discountedMRR: number;
  njapMRR: number; // legacy
  activeSubscriptions: number;
  standardCount: number;
  discountedCount: number;
  njapCount: number; // legacy
  groupBreakdown: GroupBreakdown[];
  monthlyRevenue: number;
  monthlyPaymentCount: number;
}

interface PaymentFailure {
  id: string;
  companyId: string;
  stripeInvoiceId: string;
  amount: number;
  status: string;
  failedAt: Date;
  attemptCount: number;
  failureReason: string;
  company: {
    id: string;
    name: string;
    subscriptionStatus: string;
  };
}

interface Invoice {
  id: string;
  companyId: string;
  stripeInvoiceId: string;
  amount: number;
  status: string;
  paidAt?: Date;
  createdAt: Date;
  company: {
    id: string;
    name: string;
  };
}

interface Company {
  id: string;
  name: string;
  domain: string;
  subscriptionStatus: string | null;
  stripeCustomerId: string | null;
}

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

export const BillingDashboard: React.FC = () => {
  const { token } = useAuth();
  const [revenueReport, setRevenueReport] = useState<RevenueReport | null>(null);
  const [paymentFailures, setPaymentFailures] = useState<PaymentFailure[]>([]);
  const [recentInvoices, setRecentInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [showCompanyList, setShowCompanyList] = useState(false);

  // Discount group management state
  const [showDiscountGroups, setShowDiscountGroups] = useState(false);
  const [discountGroups, setDiscountGroups] = useState<GroupBreakdown[]>([]);
  const [allDiscountGroups, setAllDiscountGroups] = useState<{ id: string; name: string; description: string | null; displayOrder: number; rateTableId?: string | null; rateTableName?: string | null }[]>([]);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [newGroupRateTableId, setNewGroupRateTableId] = useState('');
  const [editingGroup, setEditingGroup] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editRateTableId, setEditRateTableId] = useState('');
  const [groupMessage, setGroupMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [availableRateTables, setAvailableRateTables] = useState<{ id: string; name: string; isDefault: boolean }[]>([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (showDiscountGroups) {
      fetchDiscountGroups();
      fetchAvailableRateTables();
    }
  }, [showDiscountGroups]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Create axios config with authorization header
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };

      // Fetch companies first (critical for billing management)
      let companiesData: Company[] = [];
      try {
        const companiesResponse = await axios.get(`${API_BASE_URL}/api/sysadmin/companies`, config);
        console.log('✅ Companies response:', companiesResponse.data);
        companiesData = companiesResponse.data.companies || [];
        setCompanies(companiesData);
        console.log('✅ Companies set:', companiesData.length);
      } catch (err: any) {
        console.error('❌ Failed to fetch companies:', err);
        // Don't fail the whole dashboard if companies fail
      }

      // Fetch billing data (optional - dashboard can work without it)
      try {
        const [revenueResponse, failuresResponse, invoicesResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/system-admin/billing/revenue-report`, config).catch(err => {
            console.warn('Revenue report not available:', err.response?.data || err.message);
            return { data: null };
          }),
          axios.get(`${API_BASE_URL}/api/system-admin/billing/payment-failures`, config).catch(err => {
            console.warn('Payment failures not available:', err.response?.data || err.message);
            return { data: { failedPayments: [] } };
          }),
          axios.get(`${API_BASE_URL}/api/system-admin/billing/invoices?limit=10`, config).catch(err => {
            console.warn('Invoices not available:', err.response?.data || err.message);
            return { data: { invoices: [] } };
          }),
        ]);

        console.log('📊 Billing Dashboard Data:', {
          revenue: revenueResponse.data,
          failures: failuresResponse.data,
          invoices: invoicesResponse.data,
          companiesCount: companiesData.length
        });

        setRevenueReport(revenueResponse.data);
        setPaymentFailures(failuresResponse.data.failedPayments || []);
        setRecentInvoices(invoicesResponse.data.invoices || []);
      } catch (err: any) {
        console.error('❌ Error fetching billing data:', err);
        // Billing data is optional, don't set error state
      }
    } catch (error: any) {
      console.error('❌ Critical error in billing dashboard:', error);
      setError(error.response?.data?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboardData();
  };

  const fetchDiscountGroups = async () => {
    try {
      const config = { headers: { 'Authorization': `Bearer ${token}` } };
      const response = await axios.get(`${API_BASE_URL}/api/system-admin/billing/discount-groups`, config);
      setAllDiscountGroups(response.data.groups || []);
    } catch (err) {
      console.error('Error fetching discount groups:', err);
    }
  };

  const fetchAvailableRateTables = async () => {
    try {
      const config = { headers: { 'Authorization': `Bearer ${token}` } };
      const response = await axios.get(`${API_BASE_URL}/api/system-admin/billing/rate-tables`, config);
      setAvailableRateTables((response.data.rateTables || []).map((rt: any) => ({ id: rt.id, name: rt.name, isDefault: rt.isDefault })));
    } catch (err) {
      console.error('Error fetching rate tables:', err);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      setGroupMessage(null);
      const config = { headers: { 'Authorization': `Bearer ${token}` } };
      await axios.post(`${API_BASE_URL}/api/system-admin/billing/discount-groups`, {
        name: newGroupName.trim(),
        description: newGroupDescription.trim() || undefined,
        rateTableId: newGroupRateTableId || undefined,
      }, config);
      setNewGroupName('');
      setNewGroupDescription('');
      setNewGroupRateTableId('');
      setGroupMessage({ type: 'success', text: `Discount group "${newGroupName.trim()}" created` });
      await fetchDiscountGroups();
    } catch (err: any) {
      setGroupMessage({ type: 'error', text: err.response?.data?.error || 'Failed to create group' });
    }
  };

  const handleUpdateGroup = async (groupId: string) => {
    try {
      setGroupMessage(null);
      const config = { headers: { 'Authorization': `Bearer ${token}` } };
      await axios.put(`${API_BASE_URL}/api/system-admin/billing/discount-groups/${groupId}`, {
        name: editName.trim(),
        description: editDescription.trim() || undefined,
        rateTableId: editRateTableId || null,
      }, config);
      setEditingGroup(null);
      setGroupMessage({ type: 'success', text: 'Group updated' });
      await fetchDiscountGroups();
    } catch (err: any) {
      setGroupMessage({ type: 'error', text: err.response?.data?.error || 'Failed to update group' });
    }
  };

  const handleDeleteGroup = async (groupId: string, groupName: string) => {
    if (!window.confirm(`Delete discount group "${groupName}"? This cannot be undone.`)) return;
    try {
      setGroupMessage(null);
      const config = { headers: { 'Authorization': `Bearer ${token}` } };
      await axios.delete(`${API_BASE_URL}/api/system-admin/billing/discount-groups/${groupId}`, config);
      setGroupMessage({ type: 'success', text: `Discount group "${groupName}" deleted` });
      await fetchDiscountGroups();
    } catch (err: any) {
      setGroupMessage({ type: 'error', text: err.response?.data?.error || 'Failed to delete group' });
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: Date | string): string => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-600 dark:text-gray-400">Loading billing dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-6">
        <h3 className="text-lg font-semibold text-red-800 dark:text-red-200 mb-2">
          Error Loading Dashboard
        </h3>
        <p className="text-red-700 dark:text-red-300">{error}</p>
        <button
          onClick={handleRefresh}
          className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  // If a company is selected, show the management view
  if (selectedCompany) {
    return (
      <div>
        <button
          onClick={() => setSelectedCompany(null)}
          className="mb-4 flex items-center text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
        >
          <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Dashboard
        </button>
        <CompanyBillingManagement companyId={selectedCompany.id} companyName={selectedCompany.name} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Refresh Button */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Billing Dashboard
        </h2>
        <div className="flex gap-3">
          <button
            onClick={() => setShowDiscountGroups(!showDiscountGroups)}
            className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors"
          >
            {showDiscountGroups ? 'Hide Discount Groups' : 'Manage Discount Groups'}
          </button>
          <button
            onClick={() => setShowCompanyList(!showCompanyList)}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors"
          >
            {showCompanyList ? 'Hide Companies' : 'Manage Companies'}
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Revenue Metrics Cards */}
      {revenueReport && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Total MRR Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Total MRR
                </p>
                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(revenueReport.totalMRR)}
                </p>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-full">
                <svg
                  className="w-6 h-6 text-blue-600 dark:text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
            <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
              Standard: {formatCurrency(revenueReport.standardMRR)}
              {revenueReport.groupBreakdown?.length > 0
                ? revenueReport.groupBreakdown.map((g) => (
                    <span key={g.groupName}> | {g.groupName}: {formatCurrency(g.mrr)}</span>
                  ))
                : revenueReport.discountedMRR > 0
                  ? <span> | Discounted: {formatCurrency(revenueReport.discountedMRR)}</span>
                  : null}
            </div>
          </div>

          {/* Active Subscriptions Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Active Subscriptions
                </p>
                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                  {revenueReport.activeSubscriptions}
                </p>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-full">
                <svg
                  className="w-6 h-6 text-green-600 dark:text-green-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
            <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
              Standard: {revenueReport.standardCount}
              {revenueReport.groupBreakdown?.length > 0
                ? revenueReport.groupBreakdown.map((g) => (
                    <span key={g.groupName}> | {g.groupName}: {g.count}</span>
                  ))
                : revenueReport.discountedCount > 0
                  ? <span> | Discounted: {revenueReport.discountedCount}</span>
                  : null}
            </div>
          </div>

          {/* Monthly Revenue Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Monthly Revenue
                </p>
                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                  {formatCurrency(revenueReport.monthlyRevenue)}
                </p>
              </div>
              <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-full">
                <svg
                  className="w-6 h-6 text-purple-600 dark:text-purple-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  />
                </svg>
              </div>
            </div>
            <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
              {revenueReport.monthlyPaymentCount} payments this month
            </div>
          </div>

          {/* Payment Failures Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Payment Failures
                </p>
                <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                  {paymentFailures.length}
                </p>
              </div>
              <div className="p-3 bg-red-100 dark:bg-red-900/20 rounded-full">
                <svg
                  className="w-6 h-6 text-red-600 dark:text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
            <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
              Requires immediate attention
            </div>
          </div>
        </div>
      )}

      {/* Discount Group Management */}
      {showDiscountGroups && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Discount Groups
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Create and manage discount groups. Companies assigned to any group get discounted pricing.
            </p>
          </div>

          <div className="p-6 space-y-4">
            {/* Message */}
            {groupMessage && (
              <div className={`p-3 rounded-lg text-sm ${
                groupMessage.type === 'success'
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800'
                  : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
              }`}>
                {groupMessage.text}
              </div>
            )}

            {/* Existing Groups */}
            {allDiscountGroups.length > 0 && (
              <div className="space-y-2">
                {allDiscountGroups.map((group) => (
                  <div
                    key={group.id}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                  >
                    {editingGroup === group.id ? (
                      <div className="flex-1 flex items-center gap-2 flex-wrap">
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                          placeholder="Name"
                        />
                        <input
                          type="text"
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white min-w-[120px]"
                          placeholder="Description"
                        />
                        <select
                          value={editRateTableId}
                          onChange={(e) => setEditRateTableId(e.target.value)}
                          className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="">Use default rate table</option>
                          {availableRateTables.map((rt) => (
                            <option key={rt.id} value={rt.id}>
                              {rt.name}{rt.isDefault ? ' (default)' : ''}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => handleUpdateGroup(group.id)}
                          className="px-3 py-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingGroup(null)}
                          className="px-3 py-1 text-xs border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 rounded hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-3">
                          <span className="px-2.5 py-0.5 text-sm font-semibold rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
                            {group.name}
                          </span>
                          {group.description && (
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {group.description}
                            </span>
                          )}
                          {group.rateTableName && (
                            <span className="px-2 py-0.5 text-xs rounded bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300">
                              {group.rateTableName}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setEditingGroup(group.id);
                              setEditName(group.name);
                              setEditDescription(group.description || '');
                              setEditRateTableId(group.rateTableId || '');
                            }}
                            className="px-3 py-1 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteGroup(group.id, group.name)}
                            className="px-3 py-1 text-xs text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Create New Group */}
            <div className="flex items-end gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
              <div className="flex-shrink-0">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder="e.g. Consultant"
                  className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-40"
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateGroup()}
                />
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Description (optional)
                </label>
                <input
                  type="text"
                  value={newGroupDescription}
                  onChange={(e) => setNewGroupDescription(e.target.value)}
                  placeholder="e.g. Consulting partner discount"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  onKeyDown={(e) => e.key === 'Enter' && handleCreateGroup()}
                />
              </div>
              <div className="flex-shrink-0">
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                  Rate Table
                </label>
                <select
                  value={newGroupRateTableId}
                  onChange={(e) => setNewGroupRateTableId(e.target.value)}
                  className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-48"
                >
                  <option value="">Use default rate table</option>
                  {availableRateTables.map((rt) => (
                    <option key={rt.id} value={rt.id}>
                      {rt.name}{rt.isDefault ? ' (default)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={handleCreateGroup}
                disabled={!newGroupName.trim()}
                className="px-4 py-2 text-sm bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50"
              >
                Add Group
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Company Billing Management List */}
      {showCompanyList && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              All Companies - Billing Management
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Click on a company to view or create their subscription
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Domain
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Subscription Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Stripe Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {companies.map((company) => (
                  <tr key={company.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {company.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {company.domain}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {company.subscriptionStatus ? (
                        <span
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            company.subscriptionStatus === 'active'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                              : company.subscriptionStatus === 'trialing'
                              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                              : company.subscriptionStatus === 'past_due'
                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                              : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                          }`}
                        >
                          {company.subscriptionStatus}
                        </span>
                      ) : (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400">
                          No subscription
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-500 dark:text-gray-400">
                      {company.stripeCustomerId ? (
                        company.stripeCustomerId.substring(0, 20) + '...'
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">Not created</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <button
                        onClick={() => setSelectedCompany(company)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
                      >
                        {company.subscriptionStatus ? 'Manage' : 'Create Subscription'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {companies.length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No companies found
              </div>
            )}
          </div>
        </div>
      )}

      {/* Payment Failures Table */}
      {paymentFailures.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Payment Failures Requiring Attention
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Failed Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Attempts
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Reason
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {paymentFailures.map((failure) => (
                  <tr key={failure.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {failure.company.name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {failure.company.subscriptionStatus}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatCurrency(failure.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(failure.failedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400">
                        {failure.attemptCount} attempts
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400 max-w-xs truncate">
                      {failure.failureReason || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      <Link
                        to={`/system-admin/billing/companies/${failure.companyId}`}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        Manage
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Invoices Table */}
      {recentInvoices.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Recent Invoices
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Invoice ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {recentInvoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {invoice.company.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 font-mono">
                      {invoice.stripeInvoiceId.substring(0, 20)}...
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatCurrency(invoice.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          invoice.status === 'succeeded'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                            : invoice.status === 'failed'
                            ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                        }`}
                      >
                        {invoice.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(invoice.paidAt || invoice.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {paymentFailures.length === 0 && recentInvoices.length === 0 && (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
            No billing activity yet
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Billing data will appear here once subscriptions are created.
          </p>
        </div>
      )}
    </div>
  );
};
